import { asArray } from './promoPrimitives';
import { additionalRowsOf, containersOf, findMainRow, getCampaign, getOffer, getProduct, listedProductIds, negoRowsOf, newRow, getOfferInfo } from './promoModel';
import { autoContainer, toUC } from './promoUnits';
import { refreshProductValues } from './promoValues';
import { PHASE, offerPhase } from './promoPhase';

// Negotiation of the products of an offer (#23521). Port of `updateNegoHandler` (visitStep3-promo.js:2603-2743,
// product level) plus the bulk actions of the spec (§5) and RG-03. Every function returns the full list of negotiated
// rows of the offer scope, to be committed as is : `{ ok, negotiated }` or `{ ok: false, error }`.

const FIELDS = { volume: 'volume', container: 'productContainerId', location: 'locationPromoId', highlight: 'locationHighlightId' };

/**
 * Whether a product line accepts input : not in read-only mode, inside the user's write rights (TFS#20608), and, in the
 * intermediate period between nego and store (TFS#20280), only for the products already negotiated when the step loaded.
 */
export function isProductEditable(snapshot, campaign, ref, productId) {
	if (snapshot?.readonly) return false;
	if (getProduct(snapshot, productId)?.writable === false) return false;
	if (offerPhase(snapshot, campaign, ref.offerKey) === PHASE.PENDING) {
		return asArray(snapshot?.pendingEditable?.[`${ref.campaignId}-${ref.campaignOfferId}`]).includes(productId);
	}
	return true;
}

function setField(snapshot, offer, ref, row, field, value) {
	const next = { ...row };
	if (field === 'volume') next.volume = Number(value) || 0;
	else next[FIELDS[field]] = Number(value) || 0;
	next.productContainerId = autoContainer(containersOf(snapshot, offer, row.productId), next.volume, next.productContainerId);
	next.volumeUC = toUC(snapshot, row.productId, next.volume, next.productContainerId);
	return next;
}

/** RG-03 : a quantity back to 0 empties the unit, the location, the highlight, the value and the additional presences. */
function clearProduct(row) {
	return { ...row, volume: 0, productContainerId: 0, locationPromoId: 0, locationHighlightId: 0, volumeUC: 0, value: 0, _totalValue: 0 };
}

/**
 * Edits one field of a product's negotiation. `change` : `{ campaignId, campaignOfferId, offerKey, productId, field, value }`,
 * field being volume | container | location | highlight.
 */
export function applyNegoChange(snapshot, change) {
	const campaign = getCampaign(snapshot, change.campaignId);
	const offer = getOffer(campaign, change.campaignOfferId);
	if (!campaign || !offer || !FIELDS[change.field]) return { ok: false, error: 'PromoUnknownTarget' };
	const ref = { campaignId: change.campaignId, campaignOfferId: change.campaignOfferId, offerKey: change.offerKey || null };
	if (!isProductEditable(snapshot, campaign, ref, change.productId)) return { ok: false, error: 'PromoReadonly' };

	let rows = negoRowsOf(snapshot, ref).map((row) => ({ ...row }));
	const existing = findMainRow(rows, ref, change.productId);
	let main = existing || newRow(ref, change.productId, { offerKey: ref.offerKey || '' });
	if (existing && ref.offerKey && !existing.offerKey) main.offerKey = ref.offerKey;

	main = setField(snapshot, offer, ref, main, change.field, change.value);
	if (change.field === 'volume' && main.volume === 0) {
		main = clearProduct(main);
		const additional = additionalRowsOf(rows, ref, change.productId);
		rows = rows.filter((row) => !additional.includes(row));
	}

	rows = existing ? rows.map((row) => (row === existing ? main : row)) : [...rows, main];
	return { ok: true, ref, negotiated: refreshProductValues(campaign, rows, ref, change.productId) };
}

/** The bulk actions are not offered while the negotiation is being corrected (TFS#20280) : only its products already negotiated stay editable. */
const bulkAllowed = (snapshot, campaign, ref) => !snapshot?.readonly && offerPhase(snapshot, campaign, ref.offerKey) !== PHASE.PENDING;

/** Bulk action "Tout négocier" : every product not negotiated yet goes to 1, with its unit when it has only one. */
export function negotiateAll(snapshot, ref) {
	const campaign = getCampaign(snapshot, ref.campaignId);
	const offer = getOffer(campaign, ref.campaignOfferId);
	if (!campaign || !offer || !bulkAllowed(snapshot, campaign, ref)) return { ok: false, error: 'PromoReadonly' };

	let rows = negoRowsOf(snapshot, ref).map((row) => ({ ...row }));
	const offerInfo = getOfferInfo(snapshot, ref.offerKey);
	listedProductIds(snapshot, campaign, offer, offerInfo).forEach((productId) => {
		if (!isProductEditable(snapshot, campaign, ref, productId)) return;
		const existing = findMainRow(rows, ref, productId);
		if (existing && existing.volume > 0) return;
		const main = setField(snapshot, offer, ref, existing || newRow(ref, productId, { offerKey: ref.offerKey || '' }), 'volume', 1);
		rows = existing ? rows.map((row) => (row === existing ? main : row)) : [...rows, main];
		rows = refreshProductValues(campaign, rows, ref, productId);
	});
	return { ok: true, ref, negotiated: rows };
}

/**
 * Bulk action "Supprimer toutes les négos" (confirmed by the UI, RG-26) : every paying line back to 0 (RG-03), additional
 * presences and free products removed. The paying lines are kept : a local offer lists its products through them.
 */
export function clearAllNegos(snapshot, ref) {
	const campaign = getCampaign(snapshot, ref.campaignId);
	if (!campaign || !bulkAllowed(snapshot, campaign, ref)) return { ok: false, error: 'PromoReadonly' };
	const rows = negoRowsOf(snapshot, ref)
		.filter((row) => !row.additional && !row.isFree)
		.map((row) => (isProductEditable(snapshot, campaign, ref, row.productId) ? clearProduct(row) : { ...row }));
	const kept = negoRowsOf(snapshot, ref).filter((row) => (row.additional || row.isFree) && !isProductEditable(snapshot, campaign, ref, row.productId));
	return { ok: true, ref, negotiated: [...rows, ...kept.map((row) => ({ ...row }))] };
}

/** The main paying row of a product, for display. */
export const negotiatedRowOf = (snapshot, ref, productId) => findMainRow(negoRowsOf(snapshot, ref), ref, productId);
